import React, { useState, useEffect, useCallback } from 'react';
import { GraduationCap, CheckCircle, Clock, Users, X, AlertTriangle, UserX } from 'lucide-react';
import api from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { toast } from 'react-toastify';

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString();
};

/**
 * Internship Status — the internship counterpart of Probation Status.
 *
 * HR reviews interns whose internship window has ended and can:
 *  - Confirm them -> isIntern becomes false, so the flat ₹200 professional tax is
 *    deducted again from the next payroll run.
 *  - Extend (or shorten) the internship period.
 *  - Reject them -> the account is deactivated.
 */
const InternshipStatus = ({ onSidebarCountsRefresh }) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [extendTarget, setExtendTarget] = useState(null);
  const [extendDays, setExtendDays] = useState('');
  const [extendReason, setExtendReason] = useState('');
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await api.get('/admin/internship');
      setEmployees(response.data.employees || []);
    } catch (error) {
      console.error('Error fetching internship list:', error);
      toast.error('Failed to fetch internship status list');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const readyForConfirmation = employees.filter((e) => e.readyForConfirmation);

  const filterTabs = [
    { key: 'all', label: 'All', icon: Users },
    { key: 'ongoing', label: 'Ongoing', icon: GraduationCap },
    { key: 'confirmed', label: 'Confirmed', icon: CheckCircle },
    { key: 'rejected', label: 'Rejected', icon: UserX },
  ];

  const filteredEmployees = employees.filter((e) => {
    if (filter === 'all') return true;
    if (filter === 'ongoing') return e.isIntern && e.internshipStatus === 'ongoing';
    return e.internshipStatus === filter;
  });

  const handleConfirm = async (employee) => {
    if (!employee.isActive) {
      toast.error('This intern is inactive. Reactivate their account before confirming.');
      return;
    }
    if (
      !window.confirm(
        `Confirm ${employee.name} as a full-time employee? They will stop being an intern and the ₹200 professional tax will be deducted from their next payroll.`
      )
    ) {
      return;
    }
    setActionLoadingId(employee.id);
    try {
      await api.put(`/admin/internship/${employee.id}/confirm`);
      toast.success(`${employee.name} has been confirmed as a full-time employee`);
      await fetchEmployees();
      if (onSidebarCountsRefresh) onSidebarCountsRefresh();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to confirm intern');
    } finally {
      setActionLoadingId(null);
    }
  };

  const openExtendModal = (employee) => {
    if (!employee.isActive) {
      toast.error('This intern is inactive. Reactivate their account before changing the internship period.');
      return;
    }
    setExtendTarget(employee);
    setExtendDays(String(employee.internshipDays ?? 90));
    setExtendReason('');
  };

  const closeExtendModal = () => {
    setExtendTarget(null);
    setExtendDays('');
    setExtendReason('');
  };

  const handleExtendSubmit = async (e) => {
    e.preventDefault();
    if (!extendTarget) return;
    if (extendDays === '' || Number(extendDays) < 0) {
      toast.error('Enter a valid number of internship days');
      return;
    }
    setActionLoadingId(extendTarget.id);
    try {
      await api.put(`/admin/internship/${extendTarget.id}/extend`, {
        newInternshipDays: Number(extendDays),
        reason: extendReason,
      });
      toast.success(`Internship period updated for ${extendTarget.name}`);
      closeExtendModal();
      await fetchEmployees();
      if (onSidebarCountsRefresh) onSidebarCountsRefresh();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update internship period');
    } finally {
      setActionLoadingId(null);
    }
  };

  const openRejectModal = (employee) => {
    setRejectTarget(employee);
    setRejectReason('');
  };

  const closeRejectModal = () => {
    setRejectTarget(null);
    setRejectReason('');
  };

  const handleRejectSubmit = async (e) => {
    e.preventDefault();
    if (!rejectTarget) return;
    setActionLoadingId(rejectTarget.id);
    try {
      await api.put(`/admin/internship/${rejectTarget.id}/reject`, {
        reason: rejectReason,
      });
      toast.success(`${rejectTarget.name}'s internship was rejected and the account deactivated`);
      closeRejectModal();
      await fetchEmployees();
      if (onSidebarCountsRefresh) onSidebarCountsRefresh();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject internship');
    } finally {
      setActionLoadingId(null);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Internship Status</h1>
        <p className="text-gray-600">
          Review interns, confirm them as full-time employees, or change the internship period. Interns are exempt
          from the ₹200 professional tax until confirmed.
        </p>
      </div>

      {readyForConfirmation.length > 0 && !bannerDismissed && (
        <div className="flex items-start justify-between gap-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-800">
              <strong>{readyForConfirmation.length}</strong> intern{readyForConfirmation.length > 1 ? 's have' : ' has'} completed their internship period and are awaiting confirmation.
            </p>
          </div>
          <button onClick={() => setBannerDismissed(true)} className="text-amber-500 hover:text-amber-700 flex-shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {filterTabs.map(({ key, label, icon: Icon }) => {
          const isActive = filter === key;
          return (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                isActive ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Intern</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Join Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Internship Days</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Internship End Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredEmployees.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">No interns found</td>
                </tr>
              )}
              {filteredEmployees.map((employee) => (
                <tr key={employee.id} className={!employee.isActive ? 'bg-gray-50 opacity-60' : employee.readyForConfirmation ? 'bg-amber-50' : ''}>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">{employee.name}</div>
                    <div className="text-xs text-gray-500">{employee.employeeId}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(employee.joinDate)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{employee.internshipDays}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(employee.internshipEndDate)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                      employee.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {employee.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {employee.internshipStatus === 'confirmed' ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                        <CheckCircle className="h-3.5 w-3.5" /> Full-time (confirmed)
                      </span>
                    ) : employee.internshipStatus === 'rejected' ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                        <UserX className="h-3.5 w-3.5" /> Rejected
                      </span>
                    ) : employee.readyForConfirmation ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                        <Clock className="h-3.5 w-3.5" /> Ready for Confirmation
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                        <GraduationCap className="h-3.5 w-3.5" /> Ongoing
                      </span>
                    )}
                    {employee.internshipStatus === 'rejected' && employee.internshipRejectionReason && (
                      <p className="mt-1 text-xs text-red-600 max-w-xs">{employee.internshipRejectionReason}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {employee.isIntern ? (
                      <div className="flex justify-end gap-2" title={!employee.isActive ? 'Reactivate this intern to manage their internship' : undefined}>
                        <button
                          onClick={() => openExtendModal(employee)}
                          disabled={actionLoadingId === employee.id || !employee.isActive}
                          className="px-3 py-1.5 text-xs font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {employee.internshipDays ? 'Change period' : 'Set period'}
                        </button>
                        <button
                          onClick={() => openRejectModal(employee)}
                          disabled={actionLoadingId === employee.id || !employee.isActive}
                          className="px-3 py-1.5 text-xs font-semibold rounded-md border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Reject
                        </button>
                        <button
                          onClick={() => handleConfirm(employee)}
                          disabled={actionLoadingId === employee.id || !employee.isActive}
                          className="px-3 py-1.5 text-xs font-semibold rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Confirm
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">
                        {employee.internshipConfirmedAt ? `Confirmed ${formatDate(employee.internshipConfirmedAt)}` : '—'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {extendTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Change internship period - {extendTarget.name}</h3>
              <button onClick={closeExtendModal} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleExtendSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New total internship days (from join date)</label>
                <input
                  type="number"
                  min="0"
                  value={extendDays}
                  onChange={(e) => setExtendDays(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-gray-400">
                  Current: {extendTarget.internshipDays} days, ending {formatDate(extendTarget.internshipEndDate)}.
                  Enter a smaller number to shorten the internship.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
                <textarea
                  value={extendReason}
                  onChange={(e) => setExtendReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={closeExtendModal} className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoadingId === extendTarget.id}
                  className="px-4 py-2 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {rejectTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Reject internship - {rejectTarget.name}</h3>
              <button onClick={closeRejectModal} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              Rejecting ends the internship and deactivates the account. This cannot be undone from this page.
            </p>
            <form onSubmit={handleRejectSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={closeRejectModal} className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoadingId === rejectTarget.id}
                  className="px-4 py-2 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                >
                  Reject internship
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InternshipStatus;
